/**
 * `ProjectModelMaterialFork` — partial of {@link ProjectModel} owning
 * copy-on-write for a material row still owned by an asset-library entry.
 *
 * ## This has NO Unity counterpart, and that is deliberate
 *
 * Unity keeps a project's materials private purely by minting them at spawn
 * time (`CreateMaterialDataAsync`, ported in
 * `models/materials/materialCopy.ts`); its edit path overwrites whatever row
 * the snapshot names, with no ownership check at all
 * (`ProjectModel_Entities.UpdateMaterialAsync:798`). That is safe *only* while
 * the "a snapshot never names a library part's material id" invariant holds.
 *
 * Two things break the invariant, one historical and one live in both clients:
 *
 *  1. **Existing web data.** Until the placement mint landed,
 *     `placeLibraryAssetAsync` seeded snapshots with the library part rows'
 *     ids verbatim. Every model placed from the library by the web port
 *     before that fix still names the library's rows — and, because material
 *     rows are customer-scoped, so does every other project that placed it.
 *     The placement fix cannot reach data that is already written.
 *
 *  2. **`resetSceneLibraryMaterialsAsync`.** Reset Scene assigns
 *     `snapshot.materialIds := part.materialIds` (faithful port of
 *     `ProjectModel_Utils.cs:448`). After a reset, the snapshot names library
 *     rows again — so in Unity too, the next material edit writes into the
 *     library entry. Upstream simply never noticed.
 *
 * So this fork is not a workaround for a missing port; it *enforces* the
 * invariant Unity assumes rather than checks. It can only ever move a project
 * further from sharing, never toward it, and it is idempotent: once a snapshot
 * owns its copy, `isLibraryOwnedMaterial` is false and the check costs a Map
 * lookup.
 *
 * ## Known limit
 *
 * Only snapshots in **loaded** scenes are re-pointed. A scene of this project
 * that has never been opened this session keeps naming the library row until
 * it is loaded and edited. That is a bounded gap: an unloaded scene is not
 * being edited, so it cannot *contaminate* the library — it can only continue
 * to read it. Closing it properly means fetching every scene's snapshots on
 * the first edit, which is a large read for a case that heals itself.
 */
import type { EntitySnapshot } from "@models/data/entityData";
import type { MaterialDto } from "@models/data/materialDto";
import { cloneMaterialRows, remapMaterialIds } from "@models/materials/materialCopy";
import type { IStorageModel } from "@models/storage/IStorageModel";
import type { ProjectModel } from "./ProjectModel";

export class ProjectModelMaterialFork {
  /**
   * True when `materialId` is named by the PART row of a loaded asset — i.e.
   * the row belongs to an asset-library entry and is shared with every other
   * project that placed that asset.
   *
   * Reads `m_libraryOwnedMaterialIds`, the reverse index of every loaded PART
   * row's materials, maintained by `ProjectModel.registerPartMaterialIds`. A
   * Set membership test, not a scan — this runs once per material edit and a
   * slider drag fires one per pixel.
   *
   * An asset whose parts have not been registered answers `false`: without
   * the part rows we cannot know. That is why EVERY path marking an asset
   * loaded has to record its materials — see the note on the helper.
   */
  isLibraryOwnedMaterial(this: ProjectModel, materialId: string): boolean {
    if (!materialId) return false;
    return internals(this).m_libraryOwnedMaterialIds.has(materialId);
  }

  /**
   * Fork a library-owned material into a copy this project owns, re-pointing
   * every snapshot in every loaded scene that names it.
   *
   * Returns the new material id, or `null` when no fork was needed (the row is
   * already project-private) or possible (offline, or the row is unreadable).
   * A caller must therefore treat `null` as "carry on with the id you have".
   *
   * Concurrency: material edits arrive on `@input`, so a slider drag fires
   * many calls before the first write settles. Forks are de-duplicated by
   * source id through `m_materialForks`, so a burst produces exactly one copy
   * and every caller in the burst gets the same id back.
   */
  async forkLibraryMaterialAsync(
    this: ProjectModel,
    materialId: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    if (!this.isLibraryOwnedMaterial(materialId)) return null;
    const state = internals(this);

    const inFlight = state.m_materialForks.get(materialId);
    if (inFlight) return inFlight;

    const task = forkAsync(this, materialId, signal).finally(() => {
      state.m_materialForks.delete(materialId);
    });
    state.m_materialForks.set(materialId, task);
    return task;
  }
}

async function forkAsync(
  model: ProjectModel,
  materialId: string,
  signal?: AbortSignal,
): Promise<string | null> {
  const state = internals(model);
  const context = state.getStorageContext();
  if (!context) return null; // offline — nothing to isolate against

  let clone;
  try {
    clone = await cloneMaterialRows(context.storage, context.customerId, [materialId], signal);
  } catch (err) {
    console.warn(
      `[ProjectModel] could not fork library-owned material ${materialId}:`,
      err,
    );
    return null;
  }
  const copyId = clone.idMap.get(materialId);
  if (!copyId) return null; // unreadable source row — keep the shared id
  for (const copy of clone.copies) state.m_materialsById.set(copy.ID, copy);

  // Re-point every snapshot that names the library row. Persisting each
  // through `updateEntitySnapshotAsync` gives us the Materials flag on the
  // wire for free, so peers in this project follow.
  // A re-point failure THROWS rather than reporting the copy id. By the time
  // one scene has persisted, local state names the copy while the cloud may
  // still name the library row — and answering with the copy id would let the
  // caller write its edit into a row the server does not believe the snapshot
  // uses. Answering with the library id would be worse still: that is the
  // original bug. So the edit fails visibly (`MaterialView.save` toasts) and
  // the next attempt retries the whole fork.
  for (const sceneId of [...state.m_entitiesByScene.keys()]) {
    const updates: Array<{ snapshotId: string; patch: { materialIds: string[] } }> = [];
    for (const entity of model.getEntities(sceneId)) {
      for (const snapshot of walkSnapshots(entity.snapshots)) {
        if (!snapshot.materialIds.includes(materialId)) continue;
        updates.push({
          snapshotId: snapshot.id,
          patch: { materialIds: remapMaterialIds(snapshot.materialIds, clone.idMap) },
        });
      }
    }
    if (updates.length === 0) continue;
    await model.updateEntitySnapshotsAsync(sceneId, updates, signal);
  }
  return copyId;
}

function* walkSnapshots(
  snapshots: ReadonlyArray<EntitySnapshot>,
): Generator<EntitySnapshot> {
  for (const snapshot of snapshots) {
    yield snapshot;
    yield* walkSnapshots(snapshot.children);
  }
}

/** Protected `ProjectModel` state this partial reaches (mixin-cast pattern). */
interface ForkInternals {
  readonly m_libraryOwnedMaterialIds: ReadonlySet<string>;
  readonly m_materialsById: Map<string, MaterialDto>;
  readonly m_entitiesByScene: Map<string, unknown>;
  readonly m_materialForks: Map<string, Promise<string | null>>;
  getStorageContext(): {
    storage: IStorageModel;
    customerId: string;
    projectId: string;
  } | null;
}

function internals(model: ProjectModel): ForkInternals {
  return model as unknown as ForkInternals;
}
